import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from '../../../config/axios';
import {
  Box,
  Chip,
  Typography,
  CircularProgress,
  Avatar,
  Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { UrlConstant } from '../../../commom/UrlConstant';
import { getDateTimeFromTimestamp, getUserFromLocalStorage } from "../../../utils/utils";
import { CommonDataTable } from '../SharedComponent/CommonDataTable';
import ThunderIconCirlcle from '../SvgIcons/ThunderIconCirlcle';
import DownloadIconCircle from '../SvgIcons/DownloadIconCircle';
import ViewIconCircle from '../SvgIcons/ViewIconCircle';
import Successpopup from '../SharedComponent/Successpopup';
import { usePriceFormatter } from '../../../hooks';
import { useAppSelector } from '../../../Redux/hooks';
import { useDocumentPassword } from '../../../commom/hooks/useDocumentPassword';

interface Transaction {
  companyName: string;
  symbol: string;
  time: string;
  stockPrice: number;
  qty: number;
  totalCost: number;
  type: 'Buy' | 'Sell';
  logoUrl: string;
  rawType?: string;
  boosterLoanId?: string | null;
  s3Path?: string;
}

interface ExecutedOrderProps {
  mainType?: 'crypto' | 'stock';
}

const ExecutedOrder: React.FC<ExecutedOrderProps> = ({ mainType }) => {
  const user = getUserFromLocalStorage();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [popup, setPopup] = useState({ show: false, success: true, heading: '', message: '' });
  const { fetchPassword } = useDocumentPassword();
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

   const showPasswordThen = useCallback(async (action: () => void) => {
    const pwd = await fetchPassword();
    setPendingAction(() => action);
    setPopup({
      show: true,
      success: true,
      heading: 'Document Password',
      message: pwd
        ? `This document is password protected. Your PDF password is: ${pwd}`
        : 'This document is password protected — find your password under Account Settings.',
    });
  }, [fetchPassword]);

  const downloadPDF = useCallback(async (orderId: string) => {
    try {
      const response = await axios.post(
        UrlConstant.Documents.TradeConfirmationsPDF,
        { orderId },
        {
          responseType: "blob",
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Extract filename from content-disposition header
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'trade-confirmation.pdf';

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);

      setPopup({
        show: true,
        success: true,
        heading: "Success",
        message: "PDF downloaded successfully",
      });

    } catch (error: any) {
      console.error('Download error:', error);

      let errorMessage = "Failed to download PDF";

      // Handle specific error cases
      if (error.response?.status === 404) {
        errorMessage = "Trade confirmation not found";
      } else if (error.response?.status === 401) {
        errorMessage = "Session expired. Please login again";
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      setPopup({
        show: true,
        success: false,
        heading: "Error",
        message: errorMessage,
      });
    }
  }, []);

  const handleDownloadPDF = useCallback((orderId: string) => {
  showPasswordThen(() => downloadPDF(orderId));
}, [showPasswordThen, downloadPDF]);

  // Initialize formatter with appropriate asset type
  const formatter = usePriceFormatter();

  // Get currency info from Redux
  const currencyInfo = useAppSelector((state) => state.currency);

  useEffect(() => {
    const fetchExecutedOrders = async () => {
      setLoading(true);
      try {
        let formattedData: Transaction[] = [];
        if (mainType === 'crypto') {
          const response = await axios.get(`${UrlConstant.Crypto.GetCryptoOrders}?status=completed`);
          formattedData = (response.data.orders || []).map((order: any) => ({
            company: order?.crypto?.formatted_symbol || '',
            companyName: order?.crypto?.name || '',
            symbol: order?.crypto?.symbol || '',
            time: order.createdAt || '',
            stockPrice: order.avgPrice || 0,
            qty: (order.filledQuantity || order.quantity || 0).toFixed(6),

            totalCost: (order.loanAmount || 0) + (order.amount || 0),

            type: order.side?.toLowerCase() === 'buy' ? 'Buy' : 'Sell',
            orderId: order._id,
            exchange: order.exchange || '',
            logoUrl: order?.crypto?.logo || '',
            rawType: order.type || '',
            boosterLoanId: order.boosterLoanId ?? null,

            fees: order.fees || 0,
            platformFee: order.platformFee || 0,

            grossAmount: order.grossAmount || 0,
            netAmount: order.netAmount || 0,
            totalFees: order.totalFees || 0,

            s3Path: order.s3Path || null,
          }));
        } else {
          const response = await axios.post(
            UrlConstant.Order.orderList,
            {
              accountId: user?.tradingblock_application_id,
              pageIndex: '0',
              pageSize: '50',
              type: 'executed',
            },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );
          formattedData = response.data.data.map((order: any) => ({
            companyName: order.companyName || 'Unknown',
            symbol: order.symbol || 'N/A',
            time: order.time || 'N/A',
            stockPrice: order.stockPrice || 0,
            qty: order.qty || 0,
            totalCost: order.totalCost || 0,
            fees: order.fees || 0,
            type: order.type || 'Buy',
            logoUrl: order?.branding?.icon_url ?? order.branding?.logo_url,
            netAmount: order.filledAmount || 0,
          }));
        }
        setTransactions(formattedData);
      } catch (err) {
        console.error('Error fetching executed orders:', err);
        setTransactions([]);
        setError('Failed to fetch executed orders.');
      } finally {
        setLoading(false);
      }
    };

    fetchExecutedOrders();
  }, [mainType, user?.tradingblock_application_id]);

  const actionsColumn = useMemo(() => ({
    name: 'Actions',

    cell: (row: any) => {
      const actions: React.ReactNode[] = [];

      if (row.s3Path) {
        actions.push(
          <Tooltip key="view" title="View Confirmation PDF" arrow>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                showPasswordThen(()=> window.open(row.s3Path, "_blank"));
              }}
              sx={{
                cursor: "pointer",
                display: "flex",
              }}
            >
              <ViewIconCircle width={24} height={24} />
            </Box>
          </Tooltip>
        );

        actions.push(
          <Tooltip key="download" title="Download Confirmation PDF" arrow>
            <Box
              onClick={(e) => {
                e.stopPropagation();
                handleDownloadPDF(row.orderId)
              }}
              sx={{
                cursor: "pointer",
                display: "flex",
              }}
            >
              <DownloadIconCircle width={24} height={24} />
            </Box>
          </Tooltip>
        );
      }

      if (row.rawType === "LOAN" && row.boosterLoanId) {
        actions.push(
          <Tooltip key="loan" title="View Booster Loan" arrow>
            <Box
              onClick={(e) => {
                e.stopPropagation();

                navigate("/boosterLoanDetails", {
                  state: {
                    symbol: row.symbol,
                    loan_id: row.boosterLoanId,
                  },
                });
              }}
              sx={{
                cursor: "pointer",
                display: "flex",
              }}
            >
              <ThunderIconCirlcle width={24} height={24} />
            </Box>
          </Tooltip>
        );
      }

      if (!actions.length) {
        return (
          <Box
            sx={{
              px: "12px",
              py: "6px",
              borderRadius: "999px",
              background: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.05)",
              color: "rgba(255,255,255,0.28)",
              fontSize: "11px",
              fontWeight: 600,
            }}
          >
            No Actions
          </Box>
        );
      }

      return (
        <Box
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "6px",
            px: "8px",
            py: "5px",
            borderRadius: "999px",
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.06)"
          }}
        >
          {actions}
        </Box>
      );
    },
    ignoreRowClick: true,
    center: false,
    minWidth: "100px",
    grow: 0,
  }), [navigate, handleDownloadPDF]);

  const baseColumns = useMemo(() => [
    {
      name: 'Company',
      selector: (row: any) => row.companyName || '',
      cell: (row: any) => (
        <Box display="flex" alignItems="center">
          <Avatar
            src={row.logoUrl}
            alt={row.symbol}
            style={{ marginRight: '8px', height: '36px', width: '36px', fontWeight: 'bold', background: 'white' }}
          />
          <Typography style={{ fontSize: '14px', color: 'white', fontWeight: 'bold' }}>
            {row.companyName}
          </Typography>
        </Box>
      ),
      sortable: true,
      grow: 2,
      minWidth: '160px',
    },
    {
      name: 'Code',
      selector: (row: any) => row.symbol || '',
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', fontWeight: 'bold' }}>
          {row.symbol}
        </Typography>
      ),
      sortable: true,
      grow: 1,
      minWidth: '100px',
    },
    {
      name: 'Date & Time',
      selector: (row: any) => row.time || '',
      cell: (row: any) => (
        <Box>
          <Typography style={{ fontSize: '16px', color: 'white', fontWeight: 'bold' }}>
            {getDateTimeFromTimestamp(row.time, false).date} |{' '}
            <span style={{ color: '#4A7AF2' }}>
              {getDateTimeFromTimestamp(row.time, false).time}
            </span>
          </Typography>
        </Box>
      ),
      sortable: true,
      grow: 2,
      minWidth: '200px',
    },
    {
      name: 'Price',
      center: true,
      selector: (row: any) => row.stockPrice || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', fontWeight: 'bold' }}>
          {formatter.display(row.stockPrice, { showSymbol: true })}
        </Typography>
      ),
      sortable: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Qty',
      center: true,
      selector: (row: any) => row.qty || 0,
      cell: (row: any) => (
        <Typography style={{ fontSize: '16px', color: 'white', fontWeight: 'bold' }}>
          {formatter.quantity(row.qty).display}
        </Typography>
      ),
      sortable: true,
      grow: 1,
      minWidth: '150px',
    },
    {
      name: 'Amount',
      sortable: true,
      center: true,
      selector: (row: any) => row.grossAmount || 0,
      minWidth: '150px',
      cell: (row: any) => {
        const tradingFee = row.fees || 0;
        const platformFee = row.platformFee || 0;

        const totalFee = row.totalFees || 0;
        const grossAmount = row.grossAmount || 0;
        const netAmount = row.netAmount || 0;

        const hasAnyFee = totalFee > 0;

        const amountCell = (
          <Box>
            <Typography
              sx={{ color: '#fff', fontWeight: 700, fontSize: '15px', lineHeight: 1.2 }}
            >
              {formatter.amount(grossAmount).display}
            </Typography>

            {hasAnyFee && (
              <Typography sx={{ color: '#8B8CA7', fontSize: '11px' }}>
                Fee {formatter.amount(totalFee).display}
              </Typography>
            )}
          </Box>
        );

        if (!hasAnyFee) return amountCell;

        return (
          <Tooltip
            arrow
            placement="top"
            title={
              <Box sx={{ p: 1, minWidth: 220 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1, color: '#fff' }}>
                  Order Breakdown
                </Typography>

                {/* Gross */}
                <Box display="flex" justifyContent="space-between">
                  <Typography sx={{ color: '#aaa', fontSize: 12 }}>Gross Amount</Typography>
                  <Typography sx={{ color: '#fff', fontSize: 12 }}>
                    {formatter.amount(grossAmount).display}
                  </Typography>
                </Box>

                {tradingFee > 0 && (
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography sx={{ color: '#aaa', fontSize: 12 }}>Trading Fee</Typography>
                    <Typography sx={{ color: '#FFB74D', fontSize: 12 }}>
                      {formatter.amount(-tradingFee).display}
                    </Typography>
                  </Box>
                )}

                {platformFee > 0 && (
                  <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography sx={{ color: '#aaa', fontSize: 12 }}>Platform Fee</Typography>
                    <Typography sx={{ color: '#FF8A65', fontSize: 12 }}>
                      {formatter.amount(-platformFee).display}
                    </Typography>
                  </Box>
                )}

                {/* Net */}
                <Box
                  display="flex"
                  justifyContent="space-between"
                  mt={1}
                  pt={1}
                  borderTop="1px solid rgba(255,255,255,0.08)"
                >
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                    Net Amount
                  </Typography>
                  <Typography sx={{ color: '#4EDF6D', fontWeight: 700, fontSize: 13 }}>
                    {formatter.amount(row.netAmount).display}
                  </Typography>
                </Box>
              </Box>
            }
          >
            {amountCell}
          </Tooltip>
        );
      },
    },
    {
      name: 'Type',
      selector: (row: any) => row.type || '',
      cell: (row: any) => (
        <Chip
          label={row.type}
          style={{
            fontSize: '14px',
            backgroundColor: row.type === 'Buy' ? '#4EDF6D33' : '#FF2A2A33',
            color: row.type === 'Buy' ? '#4EDF6D' : '#FF2A2A',
            padding: '5px 28px',
          }}
        />
      ),
      sortable: true,
      grow: 1,
      minWidth: '120px',
    },
  ], [mainType, formatter, currencyInfo]);

  const boosterLoanColumn = useMemo(() => ({
    name: 'Booster Loan',
    cell: (row: any) => (
      <Box
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {row.rawType === 'LOAN' ? (
          row.boosterLoanId ? (
            <Chip
              label="View"
              onClick={(e) => {
                e.stopPropagation();
                navigate('/boosterLoanDetails', {
                  state: {
                    symbol: row.symbol,
                    loan_id: row.boosterLoanId,
                  },
                });
              }}
              sx={{
                backgroundColor: '#4A7AF2',
                color: '#FFFFFF',
                padding: '5px 12px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: '#3a6ae2',
                },
              }}
            />
          ) : (
            <Chip
              label="Failed"
              sx={{
                backgroundColor: '#FF2A2A33',
                color: '#FF2A2A',
                padding: '5px 12px'
              }}
            />
          )
        ) : (
          <span style={{ color: '#aaa' }}>-</span>
        )}
      </Box>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
    center: true,
    grow: 1,
    minWidth: '150px',
  }), [navigate]);

  const columns = useMemo(() => {
    return [...baseColumns, actionsColumn];
  }, [baseColumns, actionsColumn]);
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          background: 'transparent',
          borderRadius: '20px',
          border: '2px solid #FFFFFF1A',
        }}
      >
        <CircularProgress style={{ color: 'orange' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          background: 'transparent',
          borderRadius: '20px',
          border: '2px solid #FFFFFF1A',
        }}
      >
        <Typography variant="h6" style={{ color: 'red', textAlign: 'center' }}>
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <div style={{
      color: 'white',
      background: 'transparent',
      borderRadius: '20px',
      border: '2px solid #FFFFFF1A',
      padding: '20px',
      width: '100%',
      overflow: 'hidden'
    }}>
      <CommonDataTable
        data={transactions || []}
        totalRows={transactions?.length || 0}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        columns={columns}
        title="Executed Orders"
        pagination={false}
        customStyles={{
          table: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          tableWrapper: {
            style: {
              backgroundColor: 'transparent',
              width: '100%',
            },
          },
          headCells: {
            style: {
              fontSize: "12px",
              color: "white",
              fontWeight: "600",
              backgroundColor: "transparent",
              borderBottom: "1px solid #FFFFFF1A",
              paddingBottom: "12px",
              paddingTop: "16px",
              paddingLeft: "8px",
              paddingRight: "8px",
            },
          },
          cells: {
            style: {
              color: "white",
              fontSize: '16px',
              border: "none",
              padding: "12px 4px",
              backgroundColor: "transparent",
            },
          },
          rows: {
            style: {
              borderColor: "transparent !important",
              backgroundColor: 'transparent',
              width: '98% !important',
              borderRadius: "8px",
              margin: '2px 8px',
              padding: "4px",
              transition: "all 0.3s ease",
              outline: "none !important",
              '&:hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
                outline: "none !important",
              },
              '&:nth-child(even)': {
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(odd)': {
                backgroundColor: 'transparent',
                border: "1px solid transparent !important",
                borderColor: "transparent !important",
              },
              '&:nth-child(even):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
              '&:nth-child(odd):hover': {
                border: "1px solid #4A7AF2 !important",
                borderColor: "#4A7AF2 !important",
                backgroundColor: '#4A7AF21A !important',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 8px rgba(74, 122, 242, 0.15) !important',
              },
            },
          },
        }}
      />
      {popup.show && (
        <Successpopup
          onClose={() => {
            setPopup({ ...popup, show: false });
            if (pendingAction) {
              pendingAction();
              setPendingAction(null);
            }
          }}
          Success={popup.success}
          Heading={popup.heading}
          Message={popup.message}
        />
      )}
    </div>
  );
};

export default ExecutedOrder;